import { CloseIcon } from '@c/icons';
import { getGenderLabel } from '@util/createListingHelpers';
import { FilterPill } from '@util/hooks/useFilterPills';
import { PageSideFilters } from '@util/hooks/useProductsPage';
import { capitalizeFirstLetter, formatCurrency } from '@util/index';
import { Gender } from 'models/shared';

const PageFilterPills = ({
  filters,
  handleFilterRemove,
  pills,
  showClearAll,
  handleClearAll,
  createBikeDetailsRemoveHandler,
}: {
  filters: PageSideFilters;
  handleFilterRemove: (filter: { key: string; value: any }) => void;
  pills: FilterPill[];
  showClearAll: boolean;
  handleClearAll: () => void;
  createBikeDetailsRemoveHandler: (index: number) => () => void;
}) => {
  return (
    <div className="mt-4 flex gap-[0.8rem] overflow-x-scroll whitespace-nowrap sm:scrollbar-none lg:flex-wrap">
      {pills.map((pill) => {
        if (pill.key === 'rideTypes') return null;

        if (pill.key === 'bikeDetails') {
          if (
            typeof pill.index !== 'number' ||
            !Array.isArray(filters.bikeYears) ||
            !Array.isArray(filters.bikeMakes) ||
            !Array.isArray(filters.bikeModels)
          ) {
            return null;
          }
          return (
            <Pill
              key={(pill.value as string[])[0]}
              filter={(pill.value as string[])[0]}
              display={(pill.value as string[])[0]}
              onRemoveFilter={createBikeDetailsRemoveHandler(
                pill.index as number
              )}
            />
          );
        }

        if (
          pill.key === 'brands' &&
          filters.bikeMakes?.some((make) =>
            (pill.value as string[] | undefined)?.includes(make)
          )
        )
          return null;

        if (pill.value)
          if (Array.isArray(pill.value)) {
            return pill.value.map((value) => {
              const display =
                pill.key === 'genders'
                  ? getGenderLabel(value as Gender)
                  : `${value}`;

              return (
                <Pill
                  key={pill.key + value}
                  filter={`${value}`}
                  display={display.charAt(0).toUpperCase() + display.slice(1)}
                  onRemoveFilter={() => {
                    handleFilterRemove({
                      key: pill.key,
                      value: (pill.value as string[])?.filter(
                        (v: string) => v !== value
                      ),
                    });
                  }}
                />
              );
            });
          } else if (pill.key === 'product_cats') {
            const productCats = pill.value as {
              ride_type?: string;
              category?: string;
              category1?: string;
              category2?: string;
              category3?: string;
            };
            let display = '';
            if (productCats.ride_type) {
              display += `${productCats.ride_type} `;
            }
            if (productCats.category) {
              if (productCats.ride_type) display += '> '; // casual doesn't have a ride type so we don' need the > before it
              display += `${productCats.category} `;
            }
            if (productCats.category1) {
              if (productCats.category) display += '> ';
              display += `${productCats.category1} `;
            }
            if (productCats.category2) {
              if (productCats.category1) display += '> ';
              display += `${productCats.category2} `;
            }
            if (productCats.category3) {
              if (productCats.category2) display += '> ';
              display += `${productCats.category3} `;
            }
            if (Object.values(productCats).some((v) => v))
              return (
                <Pill
                  key={'product_cats'}
                  filter={String(productCats)}
                  display={display}
                  onRemoveFilter={() => {
                    handleFilterRemove({
                      key: 'product_cats',
                      value: {},
                    });
                  }}
                />
              );
          } else {
            return (
              <Pill
                key={pill.key + pill.value}
                filter={`${pill.value}`}
                display={
                  typeof pill.value === 'object' && pill.value !== null
                    ? `${(pill.value as { address: string }).address}`
                    : typeof pill.value === 'boolean'
                    ? pill.key === 'onsale'
                      ? 'On Sale'
                      : 'Recently Sold'
                    : typeof pill.value === 'number' &&
                      (pill.key.startsWith('min') || pill.key.startsWith('max'))
                    ? `${
                        pill.key.startsWith('min')
                          ? 'Min Price'
                          : pill.key.startsWith('max')
                          ? 'Max Price'
                          : ''
                      }: ${formatCurrency(pill.value)}`
                    : pill.key === 'letterSizes'
                    ? `Size: ${pill.value}`
                    : pill.key === 'numberSizes'
                    ? `Size: ${pill.value}`
                    : pill.key === 'bikeMakes' ||
                      pill.key === 'bikeModels' ||
                      pill.key === 'bikeYears'
                    ? `${pill.value}`
                    : `${capitalizeFirstLetter(pill.key)}: ${pill.value}`
                }
                onRemoveFilter={() => {
                  // Special handling for size filters to remove individual values
                  if (
                    pill.key === 'letterSizes' ||
                    pill.key === 'numberSizes'
                  ) {
                    const currentSizes = (filters[pill.key] as string[]) || [];
                    const newSizes = currentSizes.filter(
                      (size) => size !== pill.value
                    );
                    handleFilterRemove({
                      key: pill.key,
                      value: newSizes,
                    });
                  } else {
                    handleFilterRemove({
                      key: pill.key,
                      value: null,
                    });
                  }
                }}
              />
            );
          }
      })}
      {showClearAll && (
        <button
          type="button"
          className="!ml-8 rounded-[1rem] font-semibold text-zinc-700 transition-colors duration-150 hover:text-brand-secondary"
          onClick={handleClearAll}
        >
          Clear All
        </button>
      )}
    </div>
  );
};

const Pill = ({
  filter,
  display,
  onRemoveFilter,
}: {
  filter: string;
  display: string;
  onRemoveFilter: (filter: string) => void;
}) => {
  return (
    <span
      className={`flex h-[3.8rem] cursor-default items-center gap-[1.5rem] rounded-full bg-[#F1F1F1] px-4 text-[1.7rem] font-medium text-zinc-700 transition-colors duration-150 hover:bg-brand-lightest-gray`}
      key={filter}
    >
      {display}
      <button
        type="button"
        className="flex h-[2.5rem] w-[2.5rem] items-center justify-center rounded-full p-1 text-zinc-500 transition-colors duration-150 hover:bg-[#d4d4d4]"
        aria-label={`Remove '${display}' filter`}
        onClick={() => {
          onRemoveFilter(filter);
        }}
      >
        <CloseIcon />
      </button>
    </span>
  );
};

export default PageFilterPills;
